/**
 * useMedicalClearances Hook
 *
 * Manages medical clearances for clients.
 * Tracks clearance documents, expiry dates, and verification status.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";

export type ProviderType = "physician" | "physical_therapist" | "chiropractor" | "other";

export interface MedicalClearance {
  id: string;
  client_id: string;
  organization_id: string;
  clearance_date: string;
  expires_at: string | null;
  provider_name: string;
  provider_type: ProviderType;
  document_path: string | null;
  notes: string | null;
  triggered_by_parq_id: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMedicalClearanceInput {
  client_id: string;
  clearance_date: string;
  expires_at?: string | null;
  provider_name: string;
  provider_type: ProviderType;
  document_path?: string | null;
  notes?: string;
  triggered_by_parq_id?: string | null;
}

interface UseMedicalClearancesReturn {
  clearances: MedicalClearance[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  addClearance: (input: CreateMedicalClearanceInput) => Promise<MedicalClearance | null>;
  verifyClearance: (clearanceId: string) => Promise<boolean>;
  deleteClearance: (clearanceId: string) => Promise<boolean>;
  hasActiveClearance: () => boolean;
  refetch: () => Promise<void>;
}

export function useMedicalClearances(clientId: string | null): UseMedicalClearancesReturn {
  const { organization } = useOrganization();
  const [clearances, setClearances] = useState<MedicalClearance[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClearances = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      setClearances([]);
      return;
    }

    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from("medical_clearances")
        .select("*")
        .eq("client_id", clientId)
        .order("clearance_date", { ascending: false });

      if (fetchError) throw fetchError;
      setClearances((data as MedicalClearance[]) || []);
    } catch (err) {
      console.error("Error fetching medical clearances:", err);
      setError("Failed to load medical clearances");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClearances();
  }, [fetchClearances]);

  const addClearance = async (
    input: CreateMedicalClearanceInput
  ): Promise<MedicalClearance | null> => {
    if (!organization?.id) return null;

    setCreating(true);
    try {
      const { data, error } = await supabase
        .from("medical_clearances")
        .insert({
          client_id: input.client_id,
          organization_id: organization.id,
          clearance_date: input.clearance_date,
          expires_at: input.expires_at || null,
          provider_name: input.provider_name,
          provider_type: input.provider_type,
          document_path: input.document_path || null,
          notes: input.notes || null,
          triggered_by_parq_id: input.triggered_by_parq_id || null,
        })
        .select()
        .single();

      if (error) throw error;

      await fetchClearances();
      return data as MedicalClearance;
    } catch (err) {
      console.error("Error adding medical clearance:", err);
      return null;
    } finally {
      setCreating(false);
    }
  };

  const verifyClearance = async (clearanceId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from("medical_clearances")
        .update({
          verified_by: user.id,
          verified_at: new Date().toISOString(),
        })
        .eq("id", clearanceId);

      if (error) throw error;

      await fetchClearances();
      return true;
    } catch (err) {
      console.error("Error verifying clearance:", err);
      return false;
    }
  };

  const deleteClearance = async (clearanceId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("medical_clearances")
        .delete()
        .eq("id", clearanceId);

      if (error) throw error;

      await fetchClearances();
      return true;
    } catch (err) {
      console.error("Error deleting clearance:", err);
      return false;
    }
  };

  const hasActiveClearance = (): boolean => {
    const now = new Date();
    return clearances.some(
      (c) => !c.expires_at || new Date(c.expires_at) > now
    );
  };

  return {
    clearances,
    loading,
    error,
    creating,
    addClearance,
    verifyClearance,
    deleteClearance,
    hasActiveClearance,
    refetch: fetchClearances,
  };
}

// Provider type labels
export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  physician: "Physician / MD",
  physical_therapist: "Physical Therapist",
  chiropractor: "Chiropractor",
  other: "Other Provider",
};
